import React, { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { parse, stringify } from "query-string";
import { useTranslation } from "react-i18next";
import { addressLookup } from "lib/helpers";
import { selectors, actions } from "rdx";
import Lead from "models/Lead";
import msgs from "rdx/modules/leads/messages";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ApiClient from "util/ApiClient";

type QuoteFormDataT = {
  monthlyBill: string;
  firstName: string;
  lastName: string;
  streetAddress: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
};

type AddressSuggestionT = {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
};

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type QuoteModalPropsT = {
  open: boolean;
  zip: string;
  onClose: () => void;
};

const darkInputSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.06)",
    borderRadius: "10px",
    color: "white",
    fontFamily: "'Barlow', sans-serif",
    "& fieldset": { border: "1px solid rgba(255,255,255,0.1)" },
    "&:hover fieldset": { border: "1px solid rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { border: "1px solid rgba(32,194,241,0.5)" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontFamily: "'Barlow', sans-serif" },
  "& .MuiInputLabel-root.Mui-focused": { color: "rgba(32,194,241,0.85)" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
  "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(255,255,255,0.5)" },
};

export const QuoteModal = ({ open, zip, onClose }: QuoteModalPropsT) => {
  const { t } = useTranslation("static_quote_modal") as { t: TranslateFnT };
  const dispatch = useDispatch();
  const { user: userSlug } = useParams<{ user?: string }>();
  const location = useLocation();
  const history = useHistory();
  const search = parse(location.search);
  const { ref_id } = search as { ref_id?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const latestMessage = useSelector(selectors.getLatestMessageEvt) as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const zipLookupAddress = useSelector(selectors.getZipLookupAddress) as any;
  const isAvailable = (search as Record<string, string>).available === "true";
  const isUnavailable = !!(search as Record<string, string>).zip && !isAvailable;

  const [form, setForm] = useState<QuoteFormDataT>({
    monthlyBill: "",
    firstName: "",
    lastName: "",
    streetAddress: "",
    unit: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
  });
  const leadUuid = useSelector(selectors.selectLeadUuid);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<"form" | "upload" | "done">("form");
  const [submitError, setSubmitError] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (zip && open) {
      dispatch(actions.getZipLookupAddress({ zip }));
    }
  }, [zip, open, dispatch]);

  useEffect(() => {
    if (zipLookupAddress && zipLookupAddress.zip) {
      const { state, country, city, zip: lookupZip, available, invalid } = zipLookupAddress;
      const currentSearch = parse(location.search);
      const newSearch = {
        ...currentSearch,
        zip: lookupZip,
        state,
        city,
        country,
        available,
        invalid,
      };
      dispatch(actions.setZipLookupAddress({}));
      history.push({
        pathname: location.pathname,
        search: `?${stringify(newSearch)}`,
      });
    }
  }, [zipLookupAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = search as Record<string, string>;
    if (s.zip) {
      setForm((prev) => ({
        ...prev,
        zip: s.zip || prev.zip,
        city: s.city || prev.city,
        state: s.state || prev.state,
      }));
    }
  }, [search.zip, search.city, search.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setStep("form");
      setSubmitError("");
      setFile(null);
      setUploading(false);
      setUploadProgress(0);
    }
  }, [open]);

  useEffect(() => {
    if (latestMessage.message === msgs.LEAD_CREATE_SUCCESS) {
      dispatch(actions.clearLatestMessage());
      setSubmitting(false);
      setStep("upload");
    }
    if (latestMessage.message === msgs.LEAD_UPDATE_SUCCESS) {
      dispatch(actions.clearLatestMessage());
      dispatch(
        actions.setAlertMessageVisible({
          message: t("alerts.inquirySent"),
          severity: "success",
        }),
      );
      setStep("done");
    }
    if (latestMessage.message === msgs.ERROR_CREATING_LEAD) {
      dispatch(actions.clearLatestMessage());
      const backendMessage = latestMessage.error?.message as string | undefined;
      const field = latestMessage.error?.field as string | undefined;
      const concealed = !backendMessage || backendMessage === "something went wrong";

      // The API conceals validation messages as the literal "something went wrong" in
      // production. When that happens, use the field hint (still included on the response)
      // to pick a specific fallback — the duplicate-email check is the most common cause.
      let msg: string;
      if (!concealed) {
        msg = backendMessage as string;
      } else if (field === "email") {
        msg = t("form.errors.emailExists");
      } else if (field === "phone") {
        msg = t("form.errors.invalidPhone");
      } else {
        msg = t("form.errors.submitFailed");
      }

      dispatch(
        actions.setAlertMessageVisible({
          message: msg,
          duration: 6000,
          severity: "error",
        }),
      );
      setSubmitting(false);
      setSubmitError(msg);
    }
  }, [latestMessage, dispatch, t]);

  useEffect(() => {
    return () => {
      dispatch(actions.clearLatestMessage());
    };
  }, [dispatch]);

  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestionT[]>([]);

  const handleChange = (field: keyof QuoteFormDataT) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddressInput = async (_: React.SyntheticEvent, value: string) => {
    setForm((prev) => ({ ...prev, streetAddress: value }));
    const results = await addressLookup(value);
    setAddressSuggestions(results ?? []);
  };

  const handleAddressSelect = (_: React.SyntheticEvent, value: string | AddressSuggestionT | null) => {
    if (value && typeof value === "object") {
      setForm((prev) => ({
        ...prev,
        streetAddress: value.street_line,
        city: value.city,
        state: value.state,
        zip: value.zipcode,
      }));
      setAddressSuggestions([]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    const lead = {
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.phone,
      average_bill: Number(form.monthlyBill),
      category: "residential" as const,
    };
    const address = {
      street: form.streetAddress,
      unit: form.unit,
      city: form.city,
      state: form.state,
      zip: form.zip,
    };

    dispatch(
      actions.setAlertMessageVisible({
        message: t("alerts.submitting"),
        duration: 10000,
        severity: "info",
      }),
    );

    const params: Record<string, unknown> = { lead, address };
    if (ref_id) params.referrer_id = ref_id;

    Lead.createNewPublicLead({
      userSlug: userSlug || "powur.pbc",
      ...params,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const api = new ApiClient({ host: process.env.REACT_APP_POWUR_API_URL });
      const config = await api.get("/uploader_config", {
        mode: "lead_utility_bill",
        filename: file.name,
      });
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", config.signed_url);
        if (config.headers) {
          Object.entries(config.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value as string);
          });
        }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error("Upload failed"));
          }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(file);
      });
      handleUploadFinish(config.will_be);
    } catch {
      handleUploadError();
      setUploading(false);
    }
  };

  const handleUploadFinish = (url: string) => {
    dispatch(actions.updateLeadUtilityBill({ uuid: leadUuid, utility_bill_url: url, anon: true }));
  };

  const handleUploadError = () => {
    dispatch(
      actions.setAlertMessageVisible({
        message: t("alerts.uploadError"),
        severity: "error",
      }),
    );
  };

  const handleSkipUpload = () => {
    dispatch(
      actions.setAlertMessageVisible({
        message: t("alerts.inquirySent"),
        severity: "success",
      }),
    );
    setStep("done");
  };

  const handleClose = () => {
    const currentSearch = parse(location.search);
    const refId = (currentSearch as Record<string, string>).ref_id;
    history.push({
      pathname: location.pathname,
      search: refId ? `?${stringify({ ref_id: refId })}` : "",
    });
    setStep("form");
    setSubmitError("");
    onClose();
  };

  const renderStep = () => {
    if (step === "form" && isUnavailable) {
      const s = search as Record<string, string>;
      const cityName = s.invalid === "true" ? s.zip : `${s.city}, ${s.state}`;
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.2rem", sm: "1.4rem" },
              color: "white",
              mb: 3,
            }}
          >
            {t("unavailable.heading")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.4rem", sm: "1.6rem" },
              color: "white",
              mb: 3,
            }}
          >
            {cityName}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Barlow', sans-serif",
              color: "rgba(255,255,255,0.7)",
              fontSize: "0.95rem",
              lineHeight: 1.7,
              mb: 4,
            }}
          >
            {t("unavailable.expanding")}
          </Typography>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.3)",
              color: "white",
              borderRadius: "50px",
              px: 4,
              py: 1.25,
              fontFamily: "'Barlow', sans-serif",
              fontWeight: 600,
              "&:hover": { borderColor: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.05)" },
            }}
          >
            {t("unavailable.close")}
          </Button>
        </Box>
      );
    }

    if (step === "done") {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(81,242,203,0.15) 0%, rgba(32,194,241,0.15) 100%)",
              border: "1px solid rgba(81,242,203,0.3)",
              mb: 3,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40, color: "#51F2CB" }} />
          </Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "1.8rem" },
              color: "white",
              mb: 2,
            }}
          >
            {t("success.heading")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Barlow', sans-serif",
              color: "rgba(255,255,255,0.82)",
              fontSize: "1rem",
              lineHeight: 1.7,
              mb: 4,
            }}
          >
            {t("success.body")}
          </Typography>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.3)",
              color: "white",
              borderRadius: "50px",
              px: 4,
              py: 1.25,
              fontFamily: "'Barlow', sans-serif",
              fontWeight: 600,
              "&:hover": { borderColor: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.05)" },
            }}
          >
            {t("success.close")}
          </Button>
        </Box>
      );
    }

    if (step === "upload") {
      return (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.2rem", sm: "1.4rem" },
              color: "white",
              mb: 1,
            }}
          >
            {t("upload.heading")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Barlow', sans-serif",
              color: "rgba(255,255,255,0.7)",
              fontSize: "0.9rem",
              mb: 3,
            }}
          >
            {t("upload.subheading")}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box
              onDragOver={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                border: "1.5px dashed rgba(255,255,255,0.15)",
                borderRadius: "12px",
                p: 3,
                textAlign: "center",
                cursor: uploading ? "default" : "pointer",
                transition: "border-color 0.2s ease, background 0.2s ease",
                "&:hover": uploading
                  ? {}
                  : {
                      borderColor: "rgba(32,194,241,0.35)",
                      background: "rgba(32,194,241,0.04)",
                    },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 36, color: "rgba(255,255,255,0.85)", mb: 1 }} />
              {file ? (
                <Typography
                  sx={{
                    fontFamily: "'Barlow', sans-serif",
                    color: "#51F2CB",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  {file.name}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontFamily: "'Barlow', sans-serif",
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "0.85rem",
                  }}
                >
                  {t("upload.dropzone")}
                </Typography>
              )}
              {uploading && (
                <Box sx={{ mt: 1.5, mx: "auto", maxWidth: 200 }}>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      background: "rgba(255,255,255,0.1)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${uploadProgress}%`,
                        background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 100%)",
                        borderRadius: 2,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
            {file && !uploading && (
              <Box sx={{ textAlign: "center" }}>
                <Button
                  onClick={handleFileUpload}
                  sx={{
                    mt: 2,
                    background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 100%)",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.25,
                    fontFamily: "'Barlow', sans-serif",
                    fontWeight: 600,
                    color: "white",
                    textTransform: "none",
                    "&:hover": { opacity: 0.88 },
                  }}
                >
                  {t("upload.uploadButton")}
                </Button>
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              fontFamily: "'Barlow', sans-serif",
              color: "rgba(255,255,255,0.6)",
              fontSize: "0.82rem",
              fontStyle: "italic",
              mb: 2,
            }}
          >
            {t("upload.optional")}
          </Typography>

          <Button
            onClick={handleSkipUpload}
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.3)",
              color: "white",
              borderRadius: "50px",
              px: 4,
              py: 1.25,
              fontFamily: "'Barlow', sans-serif",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { borderColor: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.05)" },
            }}
          >
            {t("upload.skipButton")}
          </Button>

          <Typography
            sx={{
              textAlign: "center",
              fontFamily: "'Barlow', sans-serif",
              color: "white",
              fontSize: "0.8rem",
              mt: 3,
            }}
          >
            {t("upload.helpText")}{" "}
            <Box
              component="a"
              href="mailto:customercare@powur.com"
              sx={{ color: "#38bdf8", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              customercare@powur.com
            </Box>
          </Typography>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background: "linear-gradient(225deg, #002160 0%, #07469A 50%, #0070F4 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          m: 2,
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, position: "relative" }}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.5)",
            "&:hover": { color: "white" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {renderStep()}
        {step === "form" && isAvailable && (
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(81,242,203,0.15) 0%, rgba(32,194,241,0.15) 100%)",
                  border: "1px solid rgba(81,242,203,0.3)",
                  mb: 2,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 30, color: "#51F2CB" }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: "1.2rem", sm: "1.4rem" },
                  color: "white",
                  mb: 1,
                }}
              >
                {t("form.heading")}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Barlow', sans-serif",
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "0.9rem",
                }}
              >
                {t("form.subheading")}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("form.labels.monthlyBill")}
                  value={form.monthlyBill}
                  onChange={handleChange("monthlyBill")}
                  sx={{
                    ...darkInputSx,
                    "& input[type=number]": { MozAppearance: "textfield" },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                  required
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontFamily: "'Barlow', sans-serif" }}>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("form.labels.firstName")}
                  value={form.firstName}
                  onChange={handleChange("firstName")}
                  sx={darkInputSx}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("form.labels.lastName")}
                  value={form.lastName}
                  onChange={handleChange("lastName")}
                  sx={darkInputSx}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={addressSuggestions}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : `${option.street_line} ${option.city}, ${option.state} ${option.zipcode}`
                  }
                  inputValue={form.streetAddress}
                  onInputChange={handleAddressInput}
                  onChange={handleAddressSelect}
                  filterOptions={(x) => x}
                  componentsProps={{
                    paper: {
                      sx: {
                        background: "rgba(7,20,55,0.95)",
                        backdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "10px",
                        mt: 0.5,
                        boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                        "& .MuiAutocomplete-listbox": {
                          py: 0.5,
                          "& .MuiAutocomplete-option": {
                            fontFamily: "'Barlow', sans-serif",
                            fontSize: "0.9rem",
                            color: "rgba(255,255,255,0.85)",
                            px: 2,
                            py: 1,
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            "&:last-child": { borderBottom: "none" },
                            "&[aria-selected='true']": {
                              background: "rgba(56,189,248,0.15)",
                            },
                            "&.Mui-focused": {
                              background: "rgba(56,189,248,0.1)",
                            },
                          },
                        },
                        "& .MuiAutocomplete-noOptions": {
                          fontFamily: "'Barlow', sans-serif",
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,0.5)",
                        },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label={t("form.labels.streetAddress")} sx={darkInputSx} required />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("form.labels.unit")}
                  value={form.unit}
                  onChange={handleChange("unit")}
                  sx={darkInputSx}
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label={t("form.labels.city")}
                  value={form.city}
                  onChange={handleChange("city")}
                  sx={darkInputSx}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("form.labels.state")}
                  value={form.state}
                  onChange={handleChange("state")}
                  sx={darkInputSx}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={t("form.labels.zipCode")}
                  value={form.zip}
                  onChange={handleChange("zip")}
                  sx={darkInputSx}
                  required
                  inputProps={{ maxLength: 5, inputMode: "numeric" }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("form.labels.phone")}
                  value={form.phone}
                  onChange={handleChange("phone")}
                  sx={darkInputSx}
                  required
                  type="tel"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("form.labels.email")}
                  value={form.email}
                  onChange={handleChange("email")}
                  sx={darkInputSx}
                  required
                  type="email"
                />
              </Grid>
            </Grid>

            {submitError && (
              <Typography
                sx={{
                  color: "#f87171",
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "0.85rem",
                  mt: 1.5,
                  textAlign: "center",
                }}
              >
                {submitError}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={submitting}
              sx={{
                mt: 3,
                background: "linear-gradient(90deg, #f97316 0%, #fb923c 100%)",
                borderRadius: "50px",
                py: 1.5,
                fontWeight: 700,
                fontSize: "1.1rem",
                fontFamily: "'Barlow', sans-serif",
                color: "white",
                textTransform: "none",
                "&:hover": { opacity: 0.88 },
                "&.Mui-disabled": { opacity: 0.55, color: "white" },
              }}
            >
              {submitting ? t("form.submitting") : t("form.submitButton")}
            </Button>

            <Typography
              sx={{
                textAlign: "center",
                fontFamily: "'Barlow', sans-serif",
                color: "white",
                fontSize: "0.8rem",
                mt: 2,
              }}
            >
              {t("form.helpText")}{" "}
              <Box
                component="a"
                href="mailto:customercare@powur.com"
                sx={{ color: "#38bdf8", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                customercare@powur.com
              </Box>
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
